import { useCallback, useRef } from 'react';
import {
  CBC_AUTO_SUBMIT_FAILED_NOTICE,
  cbcPackageFingerprint,
  uploadedDocumentIdentity,
} from '../lib/cbcUploadLifecycle';
import type { CbcPackage } from '../lib/cbcTranslation';
import type { PendingCbcPackage } from './useCbcQuarantine';

export interface CbcStatusHandle {
  packages: CbcPackage[];
  isReady: boolean;
  refetchPackages: () => Promise<CbcPackage[]>;
}

interface UseCbcUploadSubmitOptions {
  cbcFieldKeys: string[];
  cbcStatus: CbcStatusHandle;
  stepKey: string;
  readOnly?: boolean;
  autoSubmit: (values: Record<string, unknown>) => Promise<unknown>;
  latestFormValuesRef: React.MutableRefObject<Record<string, unknown>>;
  mountedRef: React.MutableRefObject<boolean>;
  onAutoSubmitError: (notice: string) => void;
  onFieldRevision: () => void;
  addPending: (fieldKey: string, entry: PendingCbcPackage) => void;
  updateBaseline: (
    fieldKey: string,
    documentIdentity: string,
    fingerprint: string | null,
  ) => void;
  releaseLock: (fieldKey: string, documentIdentity: string) => void;
  removePending: (fieldKey: string) => void;
}

/** Observes advanced-upload values and starts CBC processing exactly once. */
export function useCbcUploadSubmit({
  cbcFieldKeys,
  cbcStatus: cbc,
  stepKey,
  readOnly,
  autoSubmit,
  latestFormValuesRef,
  mountedRef,
  onAutoSubmitError,
  onFieldRevision,
  addPending,
  updateBaseline,
  releaseLock,
  removePending,
}: UseCbcUploadSubmitOptions) {
  const knownIdentities = useRef(new Map<string, string>());

  const seedKnownIdentities = useCallback(
    (values: Record<string, unknown>) => {
      knownIdentities.current.clear();
      for (const fieldKey of cbcFieldKeys) {
        const identity = uploadedDocumentIdentity(values[fieldKey]);
        if (identity) knownIdentities.current.set(fieldKey, identity);
      }
    },
    [cbcFieldKeys],
  );

  const onFormChange = useCallback(
    (data: Record<string, unknown>) => {
      const uploadedFields: { fieldKey: string; documentIdentity: string }[] = [];

      for (const fieldKey of cbcFieldKeys) {
        const documentIdentity = uploadedDocumentIdentity(data[fieldKey]);
        const previousIdentity = knownIdentities.current.get(fieldKey);

        if (!documentIdentity && previousIdentity) {
          knownIdentities.current.delete(fieldKey);
          onFieldRevision();
          removePending(fieldKey);
          continue;
        }
        if (!documentIdentity || previousIdentity === documentIdentity) continue;

        knownIdentities.current.set(fieldKey, documentIdentity);
        onFieldRevision();
        uploadedFields.push({ fieldKey, documentIdentity });
      }

      if (uploadedFields.length === 0 || readOnly) return;

      const submittedAt = Date.now();
      for (const { fieldKey, documentIdentity } of uploadedFields) {
        const baseline = cbc.packages.find(
          (pkg) => pkg.stepKey === stepKey && pkg.fieldKey === fieldKey,
        );
        addPending(fieldKey, {
          documentIdentity,
          baselineFingerprint: cbc.isReady
            ? cbcPackageFingerprint(baseline)
            : undefined,
          submittedAt,
          lockActive: true,
        });
      }

      void (async () => {
        if (!cbc.isReady) {
          try {
            const baselinePackages = await cbc.refetchPackages();
            if (!mountedRef.current) return;
            for (const { fieldKey, documentIdentity } of uploadedFields) {
              const baseline = baselinePackages.find(
                (pkg) => pkg.stepKey === stepKey && pkg.fieldKey === fieldKey,
              );
              updateBaseline(
                fieldKey,
                documentIdentity,
                cbcPackageFingerprint(baseline),
              );
            }
          } catch (err) {
            console.error('[useCbcUploadSubmit] baseline refresh failed:', err);
            // Unknown baseline remains quarantined; submission still proceeds.
          }
        }

        const stillCurrent = uploadedFields.every(
          ({ fieldKey, documentIdentity }) =>
            mountedRef.current &&
            knownIdentities.current.get(fieldKey) === documentIdentity &&
            uploadedDocumentIdentity(latestFormValuesRef.current[fieldKey]) ===
              documentIdentity,
        );
        if (!stillCurrent) return;
        await autoSubmit(latestFormValuesRef.current);
      })().catch((err) => {
        console.error('[useCbcUploadSubmit] automatic submit failed:', err);
        if (!mountedRef.current) return;
        for (const { fieldKey, documentIdentity } of uploadedFields) {
          releaseLock(fieldKey, documentIdentity);
        }
        onAutoSubmitError(CBC_AUTO_SUBMIT_FAILED_NOTICE);
      });
    },
    [
      cbcFieldKeys,
      cbc.packages,
      cbc.isReady,
      cbc.refetchPackages,
      stepKey,
      readOnly,
      autoSubmit,
      latestFormValuesRef,
      mountedRef,
      onAutoSubmitError,
      onFieldRevision,
      addPending,
      updateBaseline,
      releaseLock,
      removePending,
    ],
  );

  return { seedKnownIdentities, onFormChange };
}
